import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useEffect, useState } from 'react';

import { Button } from '@chakra-ui/react';

import {
  PR_CHART_KEYS,
  PR_AGEING_CHARTS,
  PR_HEALTH_INDEX_CHARTS,
  ISSUE_CHART_KEYS,
  LOC_VS_PR_CYCLE_TIME_CHARTS,
  durationData,
  removeEmptyQueryParams,
  useQueryState,
  useDashboardStore,
  REVIEW_RESPONSIVENESS_INDEX_CHARTS,
  useMetricsTableStore,
  ISSUE_AGE_CHART_KEYS,
  ISSUE_LIFECYCLE_DIST_CHARTS,
  REQUIREMENT_STABILITY_CHARTS,
  useDetailsPageStore,
  PeriodType,
} from '@devd-client/devd/components';
import moment from 'moment';

import { PRDetails } from '../components/PR-table';
import { useAuth } from '@devd-client/devd/auth';
import IssueDetails from '../components/Issue-table';
import useMetricTableHelpers from '../components/Filters/hooks/useMetricTableHelpers';

/* eslint-disable-next-line */

export function MetricDetails() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { pathname, state } = useLocation();

  const dashboardStore = useDashboardStore();
  const detailsPageStore = useDetailsPageStore();
  const metricFilterBy = useMetricsTableStore();
  const { handleMetricFilterBy } = useMetricTableHelpers();

  const { selected, setSelected } =
    (pathname.includes('issue-details') || pathname.includes('pr-details')) &&
    searchParams.get('global') !== 'true'
      ? detailsPageStore
      : dashboardStore;

  const [appState, dispatch] = useAuth();
  const { selectedTeam, setSelectedTeam } = useDashboardStore();
  const [startDate, setStartDate] = useQueryState('start');
  const [endDate, setEndDate] = useQueryState('end');
  const [duration, setDuration] = useQueryState('duration');
  const [sprintId, setSprintId] = useQueryState('sprintId');
  const [sprintName, setSprintName] = useQueryState('sprint');
  const [periodOptions, setPeriodOptions] = useState(durationData);

  const [chartKey, setChartKey] = useQueryState('chartKey');
  const [metricKey, setMetricKey] = useQueryState('metricKey');
  const [metric, setMetric] = useQueryState('metric');
  const [heading, setHeading] = useQueryState('heading');
  const [firstOption, setFirstOption] = useQueryState('firstOption');
  const [secondOption, setSecondOption] = useQueryState('secondOption');
  const [branches, setBranches] = useQueryState('branches');
  const [teamId, setTeamId] = useQueryState('teamId');
  const [status, setStatus] = useQueryState('status');
  const [ticketType, setTicketType] = useQueryState('ticketType');
  const [prIds, setPrIds] = useQueryState('prIds');
  const [issueIds, setIssueIds] = useQueryState('issueIds');

  // below useeffect is for setting inital values for zustand from URL params.
  useEffect(() => {
    const urlMetric = metric || searchParams.get('metric');
    const urlIssueIds = issueIds || searchParams.get('issueIds');
    const urlChartKey = chartKey || searchParams.get('chartKey');
    const urlHeading = heading || searchParams.get('heading');
    const urlFirstOption = firstOption || searchParams.get('firstOption');
    const urlSecondOption = secondOption || searchParams.get('secondOption');
    const urlTicketType = ticketType || searchParams.get('ticketType');
    const urlStatus = status || searchParams.get('status');
    const urlTeamId = teamId || searchParams.get('teamId');

    if (
      pathname === '/pr-details' ||
      [
        ...PR_CHART_KEYS,
        ...PR_AGEING_CHARTS,
        ...PR_HEALTH_INDEX_CHARTS,
        ...LOC_VS_PR_CYCLE_TIME_CHARTS,
        ...REVIEW_RESPONSIVENESS_INDEX_CHARTS,
      ].includes(metric)
    ) {
      handleMetricFilterBy(
        'prFilters',
        urlFirstOption ?? '',
        urlSecondOption ? urlSecondOption : urlFirstOption ?? null,
        null,
        urlChartKey,
        metricKey,
        urlMetric,
        urlHeading
      );
      handleMetricFilterBy('status', '', status ?? null, null);
      handleMetricFilterBy('branchFilter', '', branches ?? null, null);
      metricFilterBy.setPrIds(prIds?.split(',') || null);
      metricFilterBy.setShowFilters(!prIds);
    } else if (
      pathname === '/issue-details' ||
      [
        ...ISSUE_CHART_KEYS,
        ...ISSUE_AGE_CHART_KEYS,
        ...ISSUE_LIFECYCLE_DIST_CHARTS,
        ...REQUIREMENT_STABILITY_CHARTS,
      ].includes(metric)
    ) {
      handleMetricFilterBy(
        'issueFilters',
        urlFirstOption ?? '',
        urlSecondOption ? urlSecondOption : urlFirstOption ?? null,
        urlTicketType || null,
        urlChartKey,
        '',
        urlMetric,
        urlHeading
      );
      handleMetricFilterBy('issueStatusType', '', status ?? null, null);
      metricFilterBy.setIssueIds(urlIssueIds || '');
      metricFilterBy.setShowFilters(!urlIssueIds);
      if (urlMetric) setMetric(urlMetric);
      if (urlChartKey) setChartKey(urlChartKey);
      if (urlHeading) setHeading(urlHeading);
      if (urlIssueIds) setIssueIds(urlIssueIds);
      if (urlFirstOption) setFirstOption(urlFirstOption);
      if (urlSecondOption) setSecondOption(urlSecondOption);
      if (urlTicketType) setTicketType(urlTicketType);
      if (urlStatus) setStatus(urlStatus);
      if (urlTeamId) setTeamId(urlTeamId);
    }
    handleMetricFilterBy('teams', '', teamId ?? null, null);
  }, [location.search]);

  // Below 2 useeffects are for Date filter only.
  useEffect(() => {
    if (typeof selected !== 'undefined') {
      selected.startDate && setStartDate(selected.startDate);
      selected.endDate && setEndDate(selected.endDate);
      selected.duration && setDuration(selected.duration);
      selected?.sprintId === ''
        ? setSprintId('')
        : setSprintId(selected?.sprintId);
      selected?.sprintName === ''
        ? removeEmptyQueryParams({ sprint: '' })
        : setSprintName(selected?.sprintName);
    }
  }, [selected, selectedTeam.value]);

  useEffect(() => {
    duration || selected.duration
      ? setSelected(
          periodOptions
            .filter(
              (item: PeriodType) =>
                item.duration === duration ||
                item.duration === selected.duration
            )
            .map((el: PeriodType) =>
              el.duration === 'Custom' ||
              el.duration === 'Today' ||
              el.duration === 'Month' ||
              el.duration === 'Quarter'
                ? {
                    ...el,
                    startDate: startDate || selected.startDate,
                    endDate: endDate || selected.endDate,
                  }
                : el.duration === 'Sprint'
                ? {
                    ...el,
                    startDate: startDate || selected.startDate,
                    endDate: endDate || selected.endDate,
                    sprintId: sprintId || selected.sprintId,
                    sprintName: sprintName || selected.sprintName,
                  }
                : el
            )[0]
        )
      : appState?.user?.dto?.sprint
      ? setSelected({
          ...periodOptions[6],
          startDate: moment(
            JSON.parse(appState?.user?.dto?.sprintDetail)?.startDate
          ).format('YYYY-MM-DD'),
          endDate: moment(
            JSON.parse(appState?.user?.dto?.sprintDetail)?.endDate
          ).format('YYYY-MM-DD'),
          sprintId: appState?.user?.dto?.sprint,
          sprintName: JSON.parse(appState?.user?.dto?.sprintDetail)?.sprintName,
        })
      : setSelected(periodOptions[3]);
  }, []);

  //below useEffect is for when componnent unmounts.
  useEffect(() => {
    return () => {
      metricFilterBy.reset();
    };
  }, []);

  return (
    <>
      {['contributors', 'deliverables'].includes(pathname.split('/')[1]) ? (
        <></>
      ) : searchParams.get('back') === 'hidden' ||
        window.history.length === 1 ? (
        <></>
      ) : (
        <Button
          onClick={() => navigate(-1)}
          size="sm"
          variant="link"
          w={'fit-content'}
          mr={'auto'}
          mb={2}
        >
          Back
        </Button>
      )}

      {[
        ...PR_CHART_KEYS,
        ...PR_AGEING_CHARTS,
        ...PR_HEALTH_INDEX_CHARTS,
        ...LOC_VS_PR_CYCLE_TIME_CHARTS,
        ...REVIEW_RESPONSIVENESS_INDEX_CHARTS,
      ].includes(chartKey) ? (
        <PRDetails
          sprintId={sprintId ? sprintId : selected.sprintId}
          startDate={startDate ? startDate : selected.startDate}
          endDate={endDate ? endDate : selected.endDate}
          heading={heading}
          chartKey={chartKey}
          metric={metric}
          metricKey={metricKey}
          teamId={teamId}
          status={status || 'all'}
          firstOption={firstOption}
          secondOption={secondOption}
          branches={branches}
          setFirstOption={setFirstOption}
          setSecondOption={setSecondOption}
          setBranches={setBranches}
          setTeamId={setTeamId}
          setStatus={setStatus}
          setChartKey={setChartKey}
          setMetric={setMetric}
          setHeading={setHeading}
          setMetricKey={setMetricKey}
        />
      ) : [
          ...ISSUE_CHART_KEYS,
          ...ISSUE_AGE_CHART_KEYS,
          ...ISSUE_LIFECYCLE_DIST_CHARTS,
          ...REQUIREMENT_STABILITY_CHARTS,
        ].includes(chartKey) ? (
        <IssueDetails
          sprintId={sprintId ? sprintId : selected.sprintId}
          startDate={startDate ? startDate : selected.startDate}
          endDate={endDate ? endDate : selected.endDate}
          heading={heading}
          chartKey={chartKey}
          metric={metric}
          metricKey={metricKey}
          teamId={teamId}
          status={status || 'all'}
          firstOption={firstOption}
          secondOption={secondOption}
          setFirstOption={setFirstOption}
          setSecondOption={setSecondOption}
          setTeamId={setTeamId}
          setStatus={setStatus}
          setChartKey={setChartKey}
          setMetric={setMetric}
          setHeading={setHeading}
          setMetricKey={setMetricKey}
          setTicketType={setTicketType}
        />
      ) : (
        // <Navigate to={'/git'} />
        <></>
      )}
    </>
  );
}

export default MetricDetails;
